// report_suspension (versionless; WF-015, CORE-062/063). Anyone who can see the permit
// can report a stop as a fact; the PH and RS report in their role (the capacity the server offers). The app never stands
// between a person and a physical stop: the panel says so first, and a failed report is
// shown as NOT recorded with what to do instead.
import { useId, useState } from 'react'
import { RuleRefs } from '@/components/evidence'
import { DateTimeInput } from '@/components/DateTimeInput'
import { Banner, Button, Field, KeyValue, Select, TextArea } from '@/components/ui'
import { SUSPENSION_REASONS } from '@/domain/catalogue'
import type { ActionPanelProps } from '@/features/contracts'
import { PtwError } from '@/lib/commands'
import { toLocalInput } from '@/lib/format'
import { useCommand } from '@/lib/queries'
import {
  buildReportArgs,
  currentCycle,
  cycleStateText,
  expectedVersionFor,
  reportCapacityForAction,
  reportUnanswered,
  stringArg,
  type ReportCapacity,
  type ReportInput,
} from '../logic'
import { CommandFailure, PanelFooter, SigningStatement, StopSign, Unanswered } from '../parts'

const CAPACITY_TEXT: Record<ReportCapacity, { title: string; body: string }> = {
  PH: {
    title: 'You are reporting as the Permit Holder',
    body: 'This is your formal stop. It is signed with your name and counts as your PH declaration for this stop. Leave the site and equipment safe. The Responsible Supervisor then confirms the suspension.',
  },
  RS: {
    title: 'You are reporting as the Responsible Supervisor',
    body: 'The stop is recorded under your name as RS. The Permit Holder still declares the site safe, and an RS then confirms the suspension.',
  },
  AA: {
    title: 'You are reporting as the Area Authority (historic role, older sandbox)',
    body: 'The stop is recorded under your name as AA. The Permit Holder still declares the site safe, and you then confirm the suspension.',
  },
  fact_reporter: {
    title: 'You are reporting a fact',
    body: 'You do not need a licence to report a hazard. Your name and the time are recorded with the report. The Permit Holder and the Responsible Supervisor then confirm the stop.',
  },
}

const REASON_OPTIONS = SUSPENSION_REASONS.map((o) => ({ value: o.code, label: o.label }))

export function ReportSuspensionPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const cmd = useCommand('report_suspension')
  const reasonId = useId()
  const conditionId = useId()
  const observedId = useId()
  const [input, setInput] = useState<ReportInput>(() => ({
    reason_code: '',
    reported_condition: '',
    observed_local: toLocalInput(new Date().toISOString()),
  }))
  const [capacityOverride, setCapacityOverride] = useState<ReportCapacity | null>(null)

  const capacity = capacityOverride ?? reportCapacityForAction(action)
  const cycleId = stringArg(action, 'cycle_id')
  const cycle = cycleId ? (bundle.cycles.find((c) => c.id === cycleId) ?? null) : currentCycle(bundle)
  const missing = reportUnanswered(input)
  const failed = cmd.error ? PtwError.from(cmd.error) : null

  const edit = (patch: Partial<ReportInput>) => {
    // Changed input is a new intent: never resend it under the old request key.
    if (cmd.error) cmd.resetIntent()
    setInput((prev) => ({ ...prev, ...patch }))
  }

  const send = async (cap: ReportCapacity) => {
    const args = buildReportArgs(bundle.permit.id, cycleId, input, cap)
    if (!args) return
    try {
      await cmd.run(args, expectedVersionFor('report_suspension', bundle))
      onDone()
    } catch {
      /* cmd.error holds the failure; it is shown below with the stop guidance */
    }
  }

  // A formal PH/RS report can be refused (e.g. a credential problem). The stop must still
  // be recorded, so offer the same report as a fact, which needs no licence.
  const sendAsFact = () => {
    cmd.resetIntent()
    setCapacityOverride('fact_reporter')
    void send('fact_reporter')
  }

  const cycleText = cycle
    ? `Daily cycle #${cycle.cycle_number} (${cycleStateText(cycle.cycle_state).label})`
    : 'The current daily cycle, as the server finds it'

  return (
    <div className="space-y-4">
      <section aria-label="Stop work" className="rounded-xl border-2 border-bad bg-bad p-4 text-white">
        <div className="flex items-start gap-3">
          <StopSign />
          <div>
            <p className="display text-xl font-bold leading-snug sm:text-2xl">Stop the work physically now - do not wait for the app.</p>
            <p className="mt-1 text-sm">
              Tell the people working to stop and move to a safe place if needed. Then record the stop here so the permit shows
              that work must not proceed.
            </p>
          </div>
        </div>
      </section>

      <Banner tone="info" title={CAPACITY_TEXT[capacity].title}>
        <p>
          Anyone can report a hazard. {CAPACITY_TEXT[capacity].body}
          <RuleRefs ruleIds={['CORE-062', 'CORE-063']} />
        </p>
      </Banner>

      <KeyValue
        items={[
          ['Permit', <span className="code">{bundle.permit.permit_number}</span>],
          ['Stop applies to', cycleText],
        ]}
      />

      {!action.enabled ? (
        <Banner tone="warn" title="The server noted a problem">
          <p>{action.reason ?? 'No reason was given.'}</p>
          <p className="mt-1">You can still send the report. If it cannot be recorded you will see why.</p>
        </Banner>
      ) : null}

      <Field label="Why is the work stopping?" required htmlFor={reasonId}>
        <Select
          id={reasonId}
          value={input.reason_code}
          onChange={(e) => edit({ reason_code: e.target.value })}
          placeholder="Choose the reason"
          options={REASON_OPTIONS}
        />
      </Field>

      <Field label="What did you see?" required htmlFor={conditionId} hint="Facts only: what, where, and who is affected.">
        <TextArea
          id={conditionId}
          value={input.reported_condition}
          onChange={(e) => edit({ reported_condition: e.target.value })}
          placeholder="e.g. Strong gas smell at pipe support SYN-PS-102"
        />
      </Field>

      <Field
        label="When did you see it?"
        required
        htmlFor={observedId}
        hint="Muscat time (GST). Set to when you opened this form - change it if you saw it earlier. The server records when it receives the report."
      >
        <DateTimeInput id={observedId} name="When did you see it?" value={input.observed_local} onChange={(v) => edit({ observed_local: v })} />
      </Field>

      {capacity === 'PH' ? (
        <SigningStatement>
          <p>
            You sign as <strong>Permit Holder</strong> that you have stopped the work for the reason and condition above. This is your
            signed suspension: the Responsible Supervisor can confirm it without a further PH declaration, so leave the site and equipment
            safe.
          </p>
        </SigningStatement>
      ) : null}

      {failed ? (
        <div className="space-y-2">
          <Banner tone="bad" title={failed.code === 'NETWORK' ? 'Stop report not confirmed' : 'Stop NOT recorded'}>
            Keep the work stopped. Tell the Permit Holder and the Responsible Supervisor directly - by radio, phone or in person - then try
            again.
          </Banner>
          <CommandFailure error={cmd.error} />
          {capacity !== 'fact_reporter' && failed.code !== 'NETWORK' ? (
            <Button variant="danger" block onClick={sendAsFact} loading={cmd.isPending} disabled={missing.length > 0}>
              Send it as a fact report instead
            </Button>
          ) : null}
        </div>
      ) : null}

      <Unanswered items={missing} />

      <PanelFooter>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="danger" className="min-h-14 text-lg" onClick={() => void send(capacity)} loading={cmd.isPending} disabled={missing.length > 0}>
          {failed ? 'Try again - report stop' : 'Report stop'}
        </Button>
      </PanelFooter>
    </div>
  )
}
